import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import Link from '@/components/atoms/Link/Link';
import { Autocomplete, AutocompleteOption } from '@/components/ui/custom/Autocomplete/Autocomplete';
import useDesks from '@/hooks/useDesks';
import { selectAccountSlug } from '@/redux/session/selectors';

import { useLeaveModal } from '../../../../hooks/useLeaveModal';
import { useTryItBundles } from '../../../../hooks/useTryItBundles';

const BUNDLES_DOCS_URL = 'https://docs.moveo.ai/docs/environments/context-bundles';

/** "Bundle" picker — a searchable popover combobox (same control as the context
 *  variable pickers) listing every bundle across desks, grouped by desk. When the
 *  account has no bundles it's disabled with a tooltip pointing to the bundles page.
 *  Selecting a bundle writes its variables to `context.global.bundle`; the trash
 *  button clears them again. */
export const BundleSection = () => {
  const { t } = useTranslation();
  const leaveModal = useLeaveModal();
  const slug = useSelector(selectAccountSlug);
  const { desks } = useDesks();
  const { options, selectedBundleId, selectBundle, clearBundle, hasBundles } = useTryItBundles();

  // "Create or upload" deep-links to the first environment's Context bundles page.
  const firstDeskId = desks?.[0]?.desk_id;
  const bundlesPath = firstDeskId ? `/${slug}/environments/${firstDeskId}/context_bundles` : null;
  const handleBundlesClick = (event: React.MouseEvent) => {
    // Modified clicks (new tab / new window) keep the anchor's native behavior.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    if (!bundlesPath) {
      return;
    }
    leaveModal(bundlesPath);
  };

  // Map to Autocomplete options; `category` drives the per-desk grouping.
  const autocompleteOptions = useMemo<AutocompleteOption[]>(
    () =>
      options.map((option) => ({
        value: option.value,
        label: option.label,
        category: option.deskName || undefined,
      })),
    [options]
  );

  return (
    <div className="flex max-w-sm flex-col items-start gap-2 self-stretch">
      <span className="text-sm leading-5 font-medium text-foreground">
        {t('try_it.context.bundle_label')}
      </span>

      {hasBundles ? (
        <Autocomplete
          options={autocompleteOptions}
          value={selectedBundleId}
          onChange={selectBundle}
          onClear={clearBundle}
          placeholder={t('common.select_dots')}
          searchPlaceholder={t('common.search_dots')}
          ariaLabel={t('try_it.context.bundle_label')}
        />
      ) : (
        <InfoTooltip content={t('try_it.context.bundle_empty_tooltip')} side="top">
          <span className="block w-full">
            <Autocomplete
              options={[]}
              value=""
              onChange={selectBundle}
              disabled
              placeholder={t('common.select_dots')}
              ariaLabel={t('try_it.context.bundle_label')}
            />
          </span>
        </InfoTooltip>
      )}

      <p className="text-sm leading-5 text-muted-foreground">
        {t('try_it.context.bundle_helper')}{' '}
        {bundlesPath ? (
          <Link href={bundlesPath} onClick={handleBundlesClick}>
            {t('try_it.context.here_link')}
          </Link>
        ) : (
          // Desks not resolved yet — the docs keep the sentence from dangling.
          <Link href={BUNDLES_DOCS_URL} external>
            {t('try_it.context.here_link')}
          </Link>
        )}
      </p>
    </div>
  );
};
